import React, { useCallback, useState } from 'react'

import { DeleteOutlined, DownloadOutlined, KeyOutlined, PlusOutlined } from '@ant-design/icons'
import { 
  Avatar, 
  Badge, 
  Button, 
  Card, 
  InputNumber, 
  Modal, 
  Select, 
  Space, 
  Typography, 
  Grid, 
  Row, 
  Col, 
  notification 
} from 'antd'

import {
  AccountPublic,
  BillingProduct,
  BillingSubscription,
  PaddleBillingSubscriptionItem,
  PlanType
} from '@pollination-solutions/pollination-sdk'

import { 
  formatCurrency, 
  getPlanMultiplier, 
  getPricePeriod, 
  getResourceType, 
  hasConflictingProduct, 
  hasUnusedPrices 
} from './billingUtils'

import styles from './SubscriptionItemsEditor.module.css'
import useAccountReport from 'hooks/useAccountReport'
import { useAccount } from 'hooks/useAccountProvider'
import { Link, useParams } from 'react-router-dom'

type Props = {
  subscription: BillingSubscription
  products: BillingProduct[]
  existingSubscriptions: BillingSubscription[]
  editMode: boolean
  editedItems: { [itemId: string]: { quantity: number; priceId?: string } }
  removedItems: Set<number>
  newItems: { productId?: string; priceId?: string; quantity: number; isEditing: boolean }[]
  onItemEdit: (itemIndex: number, field: 'quantity' | 'priceId', value: number | string) => void
  onAddNewItem: () => void
  onRemoveNewItem: (index: number) => void
  onUpdateNewItem: (index: number, field: 'productId' | 'priceId' | 'quantity', value: string | number) => void
  onRemoveExistingItem: (itemIndex: number) => void
  selectedPriceIds: Set<string>
  subscriptionBillingFrequency: 'monthly' | 'yearly'
  onEnterEditMode?: () => void
}


const getPastDate = (days: number) => {
  const today = new Date()
  return new Date(today.setDate(today.getDate() - days))
}

export const SubscriptionItemsEditor: React.FC<Props> = ({
  subscription,
  products,
  existingSubscriptions,
  editMode,
  editedItems,
  removedItems,
  newItems,
  onItemEdit,
  onAddNewItem,
  onRemoveNewItem,
  onUpdateNewItem,
  onRemoveExistingItem,
  selectedPriceIds,
  subscriptionBillingFrequency
}) => {
  const { account } = useAccount()
  const params = useParams<{
    accountName: string
  }>()
  
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false)
  const [itemToRemove, setItemToRemove] = useState<number | null>(null)

  // Default to 30 days for non-seat products, and 0 days for seat products
  const [reportDays, setReportDays] = useState<Map<string, number>>(
    new Map(subscription.items.map((item, index) => {
      const defaultDays = item?.product?.custom_data?.['plan_type'] !== PlanType.Seat ? 30 : 0
      return [`${item?.product?.id}-${index}`, defaultDays]
    }))
  )

  const [downloading, setDownloading] = useState<Map<string, boolean>>(new Map())
  const { getReport, reportToCsv, triggerDownload } = useAccountReport(account as AccountPublic)

  const today = new Date()

  const pastDate = {
    7: getPastDate(7),
    30: getPastDate(30),
    90: getPastDate(90),
    180: getPastDate(180),
    0: getPastDate(0)
  }

  const handleOk = (item: PaddleBillingSubscriptionItem, index: number) => {
    setDownloading(prev => {
      const newMap = new Map(prev)
      newMap.set(`${item.product?.id}-${index}`, true)
      return newMap
    })
    if (!item.product?.custom_data || !('plan_type' in item.product.custom_data)) {
      notification.error({
        message: 'Cannot determine plan type for this product.'
      })
      return
    }

    const range = reportDays.get(`${item.product?.id}-${index}`)
    const planType = item.product.custom_data['plan_type'] as PlanType
    let startDate = pastDate[range || 0]

    if (range === 0) {
      // Start date is the subscription start date if the user selects "All periods"
      startDate = new Date(subscription.started_at as string)
    }

    getReport(startDate, today, planType)
      .then(({ data }) => {
        const csv = reportToCsv(data)
        triggerDownload(`${planType || 'report'}.csv`, csv)
      }).catch((err) => {
        notification.error({
          message: err.response.data.detail,
        })
      }).finally(() => {
        setDownloading(prev => {
          const newMap = new Map(prev)
          newMap.set(`${item.product?.id}-${index}`, false)
          return newMap
        })
      })
  }

  const handleRemoveConfirmation = useCallback((itemIndex: number) => {
    setItemToRemove(itemIndex)
    setShowRemoveConfirmModal(true)
  }, [])

  const breakpoints = Grid.useBreakpoint()

  const handleConfirmRemove = useCallback(() => {
    if (itemToRemove !== null) {
      onRemoveExistingItem(itemToRemove)
      setShowRemoveConfirmModal(false)
      setItemToRemove(null)
    }
  }, [itemToRemove, onRemoveExistingItem])

  const handleQuantityChange = useCallback((itemIndex: number, value: number) => {
    const item = subscription.items[itemIndex]
    if (!item) return

    onItemEdit(itemIndex, 'quantity', value)
  }, [subscription.items, onItemEdit])

  const existingCard = (item, index) => {
    const editedItem = editedItems[index]
    const currentQuantity = editedItem?.quantity || item.quantity
    const currentPriceId = editedItem?.priceId || item.price.id

    // Find matching product for price options
    const matchingProduct = products.find(p => p.id == item.product.id)

    // Skip removed items
    if (removedItems.has(index)) return null

    // Find current price info for display
    let currentPriceAmount = item.price.unit_price?.amount || '0'
    let currentPriceInterval = item.price.billing_cycle?.interval || 'month'

    if (editedItem?.priceId && editedItem.priceId !== item.price.id) {
      const newPrice = matchingProduct?.prices?.find(p => p.id === editedItem.priceId)
      if (newPrice) {
        currentPriceAmount = newPrice.unit_price.amount
        currentPriceInterval = newPrice.billing_cycle?.interval || 'month'
      }
    }
    
    return (
      <Card key={`${item.product.id}-${index}`} 
        size="small" 
        style={{ backgroundColor: '#fafafa' }}>
        <Card.Meta
          className={styles.card}
          avatar={<Avatar src={item.product.image_url} alt={item.product.name} style={{ width: '40px', height: '40px' }} />}
          title={
            <Row style={{
              width: '100%',
            }}>
              <Col flex="auto">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Typography.Text strong ellipsis style={{
                    fontSize: '14px'
                  }}>
                    {item.product.name}
                  </Typography.Text>
                  <Space size="small">
                    <Typography.Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                      {formatCurrency(currentPriceAmount, subscription.currency_code)}
                    </Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                      / {currentPriceInterval}
                    </Typography.Text>
                  </Space>
                </Space>
              </Col>
              <Col>
                {editMode ? (
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveConfirmation(index)}
                  >
                    Remove
                  </Button>
                ): 
                <Space size="small" direction='vertical'>
                  <Space.Compact 
                    size='small'
                  >
                    <Typography.Text style={{
                      fontSize: '14px',
                      marginRight: breakpoints.xs ? 4 : 8
                    }}>Usage: </Typography.Text>
                    <Select
                      disabled={item.product.custom_data['plan_type'] === PlanType.Seat}
                      defaultValue={item.product.custom_data['plan_type'] !== PlanType.Seat 
                        ? 30
                        : 0}
                      value={reportDays.get(`${item.product.id}-${index}`)}
                      style={{ width: 120 }}
                      onChange={(value, _) => {
                        setReportDays(prev => {
                          const newMap = new Map(prev)
                          newMap.set(`${item.product.id}-${index}`, value)
                          return newMap
                        })
                      }}
                      options={[
                        {
                          value: 7,
                          label: 'Last 7 days',
                        },
                        {
                          value: 30,
                          label: 'Last 30 days',
                        },
                        {
                          value: 90,
                          label: 'Last 90 days',
                        },
                        {
                          value: 180,
                          label: 'Last 180 days',
                        },
                        {
                          value: 0,
                          label: 'All periods',
                        }
                      ]}
                    />
                    <Button
                      title='Download report'
                      type="primary"
                      aria-label='Download report'
                      icon={<DownloadOutlined />}
                      loading={downloading.get(`${item.product.id}-${index}`) || false}
                      onClick={() => handleOk(item, index)}
                    />
                  </Space.Compact>
                  {!([PlanType.Seat, PlanType.CloudCompute].includes(item.product.custom_data['plan_type'])) &&
                  <Link to={`/${params?.accountName}/licenses`}>
                    <Button size="small" 
                      icon={<KeyOutlined />}
                      style={{
                        width: '100%',
                      }}>Manage License Pool</Button>
                  </Link>}
                </Space>}
                </Col>
            </Row>
          }
        />

        <Space 
          direction='vertical'
          style={{ 
            width: '100%', 
            marginTop: 24
          }}>
            {matchingProduct?.prices && matchingProduct.prices.length > 1 && (
              <Space 
                direction={'vertical'}
                size="small" style={{ width: '100%' }}>
                <Typography.Text strong>Price Plan: </Typography.Text>
                <Select
                  style={{ width: '100%' }}
                  value={currentPriceId}
                  disabled={!editMode || matchingProduct.prices?.filter(price => {
                      const period = getPricePeriod(price)
                      return period === subscriptionBillingFrequency
                    })?.length <= 1}
                  onChange={(value) => onItemEdit(index, 'priceId', value)}
                  size="small"
                >
                  {matchingProduct.prices
                    ?.filter(price => {
                      const period = getPricePeriod(price)
                      return period === subscriptionBillingFrequency
                    })
                    ?.map(price => (
                      <Select.Option
                        key={price.id}
                        value={price.id}
                        disabled={selectedPriceIds.has(price.id) && price.id !== currentPriceId}
                      >
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{price.name}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {formatCurrency(price.unit_price.amount, subscription.currency_code)}
                            /{price.billing_cycle?.interval || 'month'}
                          </div>
                          {selectedPriceIds.has(price.id) && price.id !== currentPriceId && (
                            <div style={{ fontSize: '10px', color: '#ff4d4f' }}>Already selected</div>
                          )}
                        </div>
                      </Select.Option>
                    ))}
                </Select>
              </Space>
            )}

            <Space size="small" style={{ width: '100%', justifyContent: 'flex-end' }}>
              <InputNumber
                min={1}
                className={styles.input}
                disabled={!editMode || item.product.custom_data['plan_type'] === PlanType.CloudCompute}
                style={{
                  width: '100px'
                }}
                addonBefore={<Typography.Text strong>Qty</Typography.Text>}
                value={currentQuantity}
                onChange={(value) => handleQuantityChange(index, value || item.quantity)}
                size="small"
              />
            </Space>
        </Space>
        
      </Card>
    )
  }

  return (
    <>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {editMode && (
          <Space style={{ width: '100%', justifyContent: 'flex-start' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={onAddNewItem}
            >
              Add Product
            </Button>
          </Space>
        )}

        {subscription.items?.map(existingCard)}

        {/* New Items */}
        {editMode && newItems.map((newItem, index) => {
          const selectedProduct = products.find(p => p.id === newItem.productId)

          return (
            <Card key={`new-${index}`} size="small" style={{
              backgroundColor: '#e6f7ff',
              border: '2px dashed #1890ff',
              marginBottom: '8px'
            }}>
              <Space direction="vertical" style={{ width: '100%' }} size="small">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography.Text strong style={{ color: '#1890ff' }}>New Product</Typography.Text>
                  <Button
                    danger
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => onRemoveNewItem(index)}
                  />
                </div>
                  <Typography.Text strong>Product: </Typography.Text>
                  <Select
                    placeholder="Select product"
                    value={newItem.productId}
                    style={{
                      width: '100%'
                    }}
                    size="small"
                    onChange={(value) => onUpdateNewItem(index, 'productId', value)}
                  >
                    {products
                      .filter(product => !hasConflictingProduct(product.id, subscription.id, existingSubscriptions) && hasUnusedPrices(product, selectedPriceIds, subscriptionBillingFrequency))
                      // Workaround for now to hide cloud compute products from the product selection dropdown. This is because we don't want users to be able to add cloud compute products to their subscription at this time.
                      .filter(product => product.plan_type !== PlanType.CloudCompute)
                      .map(product => (
                        <Select.Option key={product.id} value={product.id} 
                          style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <div>   
                            <Typography.Text strong ellipsis>{product.name}</Typography.Text>
                            <br />
                            <Typography.Text type='secondary' ellipsis>{product.description?.split('.')[0] + '.'}</Typography.Text>
                          </div>
                      </Select.Option>
                    ))}
                  </Select>

                  <Typography.Text strong>Price Plan: </Typography.Text>
                  <Select
                    placeholder="Select price plan"
                    value={newItem.priceId}
                    onChange={(value) => onUpdateNewItem(index, 'priceId', value)}
                    style={{ width: '100%' }}
                    size="small"
                    disabled={!selectedProduct}
                  >
                    {selectedProduct?.prices?.length === 0 ? (
                      <Select.Option value="" disabled>
                        No prices available for this product
                      </Select.Option>
                    ) : selectedProduct?.prices
                      ?.filter(price => {
                        return getPricePeriod(price) === subscriptionBillingFrequency
                      })?.length === 0 ? (
                      <Select.Option value="" disabled>
                        No {subscriptionBillingFrequency} prices available
                      </Select.Option>
                    ) : selectedProduct?.prices
                      ?.filter(price => {
                        return getPricePeriod(price) === subscriptionBillingFrequency
                      })
                      ?.map(price => (
                        <Select.Option
                          key={price.id}
                          value={price.id}
                          disabled={selectedPriceIds.has(price.id)}
                        >
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{price.name}</div>
                            <div style={{ fontSize: '11px', color: '#666' }}>
                              {formatCurrency(price.unit_price.amount, subscription.currency_code)}
                              /{price.billing_cycle?.interval || 'month'}
                            </div>
                            {selectedPriceIds.has(price.id) && (
                              <div style={{ fontSize: '10px', color: '#ff4d4f' }}>Already selected</div>
                            )}
                          </div>
                        </Select.Option>
                      ))}
                  </Select>

                  <Space style={{ width: '100%', justifyContent: 'flex-end', marginTop: 8 }}>
                    <InputNumber
                      min={1}
                      value={newItem.quantity}
                      className={styles.input}
                      addonBefore={<Typography.Text strong>Qty</Typography.Text>}
                      onChange={(value) => onUpdateNewItem(index, 'quantity', value || 1)}
                      style={{
                        width: '100px'
                      }}
                      size="small"
                    />
                  </Space>

                {selectedProduct && newItem.priceId && (() => {
                  const selectedPrice = selectedProduct.prices?.find(p => p.id === newItem.priceId)
                  if (!selectedPrice) return null

                  const itemMultiplier = getPlanMultiplier(selectedPrice)
                  const itemResources = itemMultiplier * newItem.quantity
                  const resourceType = getResourceType(selectedProduct)

                  return (
                    <div>
                      <Space size="small">
                        <Typography.Title level={5} style={{ margin: 0, color: '#1890ff' }}>
                          {formatCurrency(parseFloat(selectedPrice.unit_price.amount) * newItem.quantity, subscription.currency_code)}
                        </Typography.Title>
                        <Typography.Text type="secondary" style={{ fontSize: '12px', fontWeight: 'normal' }}>
                          / {selectedPrice.billing_cycle?.interval || 'month'}
                        </Typography.Text>
                      </Space>

                      {itemResources > 0 && (
                        <Badge
                          count={resourceType.type === 'bundled_license'
                            ? `${itemResources} Rhino + ${itemResources} Revit licenses`
                            : `${itemResources} ${itemResources === 1 ? resourceType.singular : resourceType.plural}`
                          }
                          style={{
                            backgroundColor: '#1890ff',
                            fontSize: '9px',
                            height: '14px',
                            lineHeight: '14px',
                            marginLeft: 8
                          }}
                        />
                      )}
                    </div>
                  )
                })()}
              </Space>
            </Card>
          )
        })}
      </Space>

      {/* Remove Confirmation Modal */}
      <Modal
        title="Remove Product"
        open={showRemoveConfirmModal}
        onCancel={() => {
          setShowRemoveConfirmModal(false)
          setItemToRemove(null)
        }}
        footer={[
          <Button key="back" onClick={() => {
            setShowRemoveConfirmModal(false)
            setItemToRemove(null)
          }}>
            Cancel
          </Button>,
          <Button
            key="submit"
            danger
            type="primary"
            onClick={handleConfirmRemove}
          >
            Remove Product
          </Button>,
        ]}
      >
        <div style={{ marginBottom: '16px' }}>
          <Typography.Text strong style={{ color: '#ff4d4f' }}>
            ⚠️ This will remove the product from your subscription!
          </Typography.Text>
        </div>
        <Typography.Paragraph>
          Are you sure you want to remove this product? This change will be applied when you save your subscription updates.
        </Typography.Paragraph>
      </Modal>
    </>
  )
}
